import type { ScaleOption, UpscaleMode } from "@/lib/utils";
import { loadImageElement } from "@/lib/utils";

export const MAX_OUTPUT_PIXELS = 24_000_000;

export interface LocalUpscaleOptions {
  image: HTMLImageElement;
  scale: ScaleOption;
  mode: UpscaleMode;
  enhance: boolean;
  signal?: AbortSignal;
  onProgress?: (percent: number, label: string) => void;
}

type ModelScale = 2 | 4;

function assertNotAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    const error = new Error("Cancelled.");
    error.name = "AbortError";
    throw error;
  }
}

async function loadEsrganModel(scale: ModelScale) {
  if (scale === 2) {
    return (await import("@upscalerjs/esrgan-slim/2x")).default;
  }
  return (await import("@upscalerjs/esrgan-slim/4x")).default;
}

function assertFitsMemory(width: number, height: number, scale: number) {
  const outW = width * scale;
  const outH = height * scale;
  if (outW * outH > MAX_OUTPUT_PIXELS) {
    throw new Error(
      `${scale}× would produce ${outW.toLocaleString()}×${outH.toLocaleString()} — too large for the browser. Try a smaller scale or crop the image.`,
    );
  }
}

export async function runLocalUpscale({
  image,
  scale,
  mode,
  enhance,
  signal,
  onProgress,
}: LocalUpscaleOptions): Promise<Blob> {
  assertNotAborted(signal);
  assertFitsMemory(image.naturalWidth, image.naturalHeight, scale);

  const [{ default: Upscaler }, tf] = await Promise.all([
    import("upscaler"),
    import("@tensorflow/tfjs"),
  ]);
  assertNotAborted(signal);

  onProgress?.(8, "Warming WebGL backend…");
  try {
    await tf.setBackend("webgl");
    await tf.ready();
  } catch {
    await tf.setBackend("cpu");
    await tf.ready();
  }

  const passes: ModelScale[] = scale === 8 ? [4, 2] : [scale];
  let current: HTMLImageElement | HTMLCanvasElement = image;
  let blob: Blob | null = null;
  const tempUrls: string[] = [];

  try {
    for (let i = 0; i < passes.length; i += 1) {
      const passScale = passes[i];
      const start = 12 + (i / passes.length) * 76;
      const span = 76 / passes.length;
      onProgress?.(start, `Loading ESRGAN ${passScale}× weights…`);

      const definition = (await loadEsrganModel(passScale)) as object;
      const upscaler = new Upscaler({
        model: {
          ...definition,
          path: `/models/esrgan-slim/x${passScale}/model.json`,
        },
      });

      try {
        const src = await upscaler.upscale(current, {
          output: "base64",
          patchSize: passScale === 4 ? 64 : 96,
          padding: 4,
          progress: (rate: number) => {
            const pct = start + Math.round(Math.min(1, Math.max(0, rate)) * span);
            const passLabel =
              passes.length > 1 ? `pass ${i + 1}/${passes.length} · ` : "";
            onProgress?.(pct, `${passLabel}${Math.round(rate * 100)}%`);
          },
        });
        blob = await dataUrlToBlob(String(src));
      } finally {
        await upscaler.dispose();
      }

      assertNotAborted(signal);

      if (i < passes.length - 1 && blob) {
        const url = URL.createObjectURL(blob);
        tempUrls.push(url);
        current = await loadImageElement(url);
      }
    }

    if (!blob) throw new Error("ESRGAN produced no image.");

    onProgress?.(92, "Encoding output…");
    if (!enhance) return blob;

    onProgress?.(
      96,
      mode === "anime" ? "Sharpening line art…" : "Enhancing detail…",
    );
    return sharpenBlob(blob, mode === "anime" ? 0.28 : 0.42);
  } finally {
    for (const url of tempUrls) URL.revokeObjectURL(url);
  }
}

async function dataUrlToBlob(src: string): Promise<Blob> {
  const response = await fetch(src);
  const blob = await response.blob();
  if (!blob.type.startsWith("image/") && !src.startsWith("data:image")) {
    throw new Error("The local model did not return an image.");
  }
  return blob;
}

async function sharpenBlob(blob: Blob, amount: number): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    bitmap.close();
    return blob;
  }

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const src = frame.data;
  const copy = new Uint8ClampedArray(src);
  const w = canvas.width;
  const h = canvas.height;
  const kernel = [
    0,
    -amount,
    0,
    -amount,
    1 + 4 * amount,
    -amount,
    0,
    -amount,
    0,
  ];

  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = (y * w + x) * 4;
      for (let c = 0; c < 3; c += 1) {
        let acc = 0;
        let k = 0;
        for (let ky = -1; ky <= 1; ky += 1) {
          for (let kx = -1; kx <= 1; kx += 1) {
            acc += copy[((y + ky) * w + (x + kx)) * 4 + c] * kernel[k];
            k += 1;
          }
        }
        src[i + c] = acc < 0 ? 0 : acc > 255 ? 255 : acc;
      }
    }
  }

  ctx.putImageData(frame, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result ? resolve(result) : reject(new Error("Could not encode sharpened image.")),
      "image/png",
    );
  });
}
